import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import AccountBlockedModal from '@components/common/Modals/AccountBlockedModal/AccountBlockedModal';
import ValidationApplication from '@components/lpc/Authentication/LeftSide/Login/TfaConfirmation/ValidationApplication/ValidationApplication';
import ValidationBackupCodes from '@components/lpc/Authentication/LeftSide/Login/TfaConfirmation/ValidationBackupCodes/ValidationBackupCodes';
import ValidationSms from '@components/lpc/Authentication/LeftSide/Login/TfaConfirmation/ValidationSms/ValidationSms';
import Header from '@components/lpc/DoubleAuthentication/Header/Header';
import { DoubleAuthenticationModel } from '@models/UserModel/DoubleAuthenticationModels/DoubleAuthenticationModel';
import authService from '@services/domain/Lpc/AuthService';
import { getRoute, ROUTE } from '@services/http/Route';
import { getItemFromStorage } from '@services/utils/StorageService';

import css from './TfaConfirmation.module.scss';

type TfaConfirmationProps = {
    autoCreate: any;
    phoneNumber: string;
    email: string;
    password: string;
    stayConnected: boolean;
    tfaType: number;
};

const TfaConfirmation = ({
    tfaType,
    email,
    password,
    phoneNumber,
    stayConnected,
    autoCreate,
}: TfaConfirmationProps) => {
    const { addToast } = useToasts();
    const router = useRouter();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [smsTimer, setSmsTimer] = useState<number>(0);
    const { t } = useTranslateLpc(['lpc-authentication', 'common']);
    const [isAccountBlockedModalVisible, setIsAccountBlockedModalVisible] = useState<boolean>(false);
    const [query, setQuery] = useState<string | string[]>(null);
    const [submissionState, setSubmissionState] = useState({
        error: '',
        type: 0,
        retries: 3,
        isFeedBackVisible: false,
    });
    const [code, setCode] = useState<string>('');
    const [chosenTfaType, setChosenTfaType] = useState<number>(tfaType);

    const onHideFeedBack = () => {
        setSubmissionState({
            ...submissionState,
            isFeedBackVisible: false,
        });
    };

    useEffect(() => {
        setChosenTfaType(
            tfaType === DoubleAuthenticationModel.BOTH ? DoubleAuthenticationModel.GOOGLE_AUTHENTICATOR : tfaType,
        );
    }, [tfaType]);

    const onSuccess = () => {
        setSubmissionState({
            error: null,
            type: tfaType,
            retries: null,
            isFeedBackVisible: true,
        });
        if (getItemFromStorage('session', 'temporaryPot')) {
            return router.push(getRoute(ROUTE.LPC.POT.CREATE_POT + (autoCreate ? '?action=autoCreate' : ''))).then();
        }

        if (query) {
            return router.push(getRoute(ROUTE.LPC.POT.POT, query, `?participate=${true}`)).then();
        }

        if (getItemFromStorage('local', 'payWithPot')) {
            return router
                .push(getRoute(ROUTE.LPC.POT.SPENDING_PARTNER_LIMONETIK, getItemFromStorage('local', 'payWithPot')))
                .then();
        }
        if (router.query.urlReturn) {
            // @ts-ignore
            return window.location.assign(router.query.urlReturn);
        }
        authService
            .userHasPots()
            .then((res) => {
                res.data
                    ? router.push(getRoute(ROUTE.LPC.DASHBOARD.MY_PARAMS)).then()
                    : router.push(getRoute(ROUTE.LPC.POT.CREATE_POT)).then();
            })
            .catch(() => {
                router.push(getRoute(ROUTE.LPC.POT.CREATE_POT)).then();
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        setQuery(router.query?.potRef);
    }, [router.query?.potRef]);

    const onError = (err) => {
        setSubmissionState({
            error: t(`errors.${err.code}`),
            type: tfaType,
            retries: submissionState.retries - 1,
            isFeedBackVisible: true,
        });

        submissionState.retries === 1 && setIsAccountBlockedModalVisible(true);
    };

    const sendSms = () => {
        authService.sendSms(email).catch((err) => {
            err.code === 'SMS_LIMIT_EXCEEDED'
                ? setIsAccountBlockedModalVisible(true)
                : addToast(
                      t(
                          `errors.${
                              tfaType === DoubleAuthenticationModel.BOTH
                                  ? 'SMS_RESEND_ERROR_CHANGE_MODE'
                                  : 'SMS_RESEND_ERROR'
                          }`,
                      ),
                      {
                          appearance: 'error',
                          autoDismiss: true,
                      },
                  );
        });
    };

    const onSubmit = () => {
        const body = {
            email: email,
            password: password,
            stayConnected: stayConnected,
            chosenTfaType: chosenTfaType,
            tfaCode: code,
        };

        setIsLoading(true);
        return authService
            .signIn(body)
            .then((res) => {
                if (res.data.needTfaActivation) {
                    return router.push({ pathname: getRoute(ROUTE.LPC.AUTH.TFA), query: router.query });
                } else {
                    onSuccess();
                }
            })
            .catch((err) => onError(err))
            .finally(() => setIsLoading(false));
    };

    return (
        <div>
            <Header />
            <div className={css.tfa_confirmation_wrapper}>
                {chosenTfaType === DoubleAuthenticationModel.GOOGLE_AUTHENTICATOR && (
                    <ValidationApplication
                        submissionState={submissionState}
                        onHideFeedBack={onHideFeedBack}
                        tfaCode={code}
                        onSubmit={onSubmit}
                        onSetCode={setCode}
                        tfaType={tfaType}
                        isLoading={isLoading}
                        onChangeMode={(mode) => {
                            setChosenTfaType(mode);
                            onHideFeedBack();
                            smsTimer === 0 && sendSms();
                        }}
                    />
                )}
                {chosenTfaType === DoubleAuthenticationModel.SMS && (
                    <ValidationSms
                        setSmsTimer={setSmsTimer}
                        submissionState={submissionState}
                        onHideFeedBack={onHideFeedBack}
                        tfaCode={code}
                        onSubmit={onSubmit}
                        onSetCode={setCode}
                        email={email}
                        tfaType={tfaType}
                        isLoading={isLoading}
                        phoneNumber={phoneNumber}
                        onChangeMode={(mode) => {
                            setChosenTfaType(mode);
                            onHideFeedBack();
                        }}
                    />
                )}
                {chosenTfaType === DoubleAuthenticationModel.BACKUP_CODE && (
                    <ValidationBackupCodes
                        submissionState={submissionState}
                        onHideFeedBack={onHideFeedBack}
                        tfaCode={code}
                        onSubmit={onSubmit}
                        onSetCode={setCode}
                        isLoading={isLoading}
                        onChangeMode={() => {
                            setChosenTfaType(
                                tfaType === DoubleAuthenticationModel.BOTH
                                    ? DoubleAuthenticationModel.GOOGLE_AUTHENTICATOR
                                    : tfaType,
                            );
                        }}
                    />
                )}
            </div>
            {isAccountBlockedModalVisible && (
                <AccountBlockedModal
                    isAccountBlockedModalVisible={isAccountBlockedModalVisible}
                    onHide={() => setIsAccountBlockedModalVisible(false)}
                />
            )}
        </div>
    );
};

export default TfaConfirmation;
